import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BookOpen, Database, CheckCircle, TrendingUp, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const KnowledgeDashboard = () => {
  const [stats, setStats] = useState(null);
  const [topVehicles, setTopVehicles] = useState([]);
  const [qualityTrend, setQualityTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data in parallel
      const [statsRes, vehiclesRes, trendRes] = await Promise.all([
        axios.get(`${API}/admin/knowledge/dashboard-stats`, { headers }),
        axios.get(`${API}/admin/knowledge/top-vehicles?limit=10`, { headers }),
        axios.get(`${API}/admin/knowledge/quality-trend?days=30`, { headers })
      ]);

      setStats(statsRes.data);
      setTopVehicles(vehiclesRes.data);
      setQualityTrend(trendRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteMissingData = async () => {
    try {
      setCompleting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/knowledge/vehicle-specs/complete-missing-data`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      
      // Refresh dashboard data after a delay
      setTimeout(() => {
        fetchDashboardData();
      }, 3000);
      
    } catch (error) {
      console.error('Failed to complete missing data:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Starten der Datenvervollständigung');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="max-w-7xl mx-auto">
          <p style={{ color: '#94A3B8' }}>Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  const successRate = stats?.success_rate || 0;
  const avgCompleteness = (stats?.avg_completeness || 0) * 100;

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            📊 Wissens-Dashboard
          </h2>
          <Button
            onClick={handleCompleteMissingData}
            disabled={completing}
            className="flex items-center gap-2"
            style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
          >
            <RefreshCw className={`w-4 h-4 ${completing ? 'animate-spin' : ''}`} />
            {completing ? 'Vervollständige Daten...' : 'Fehlende Daten vervollständigen'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Knowledge Base Card */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Knowledge Base</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: '#E6EDF3' }}>
                    {stats?.total_knowledge_items || 0}
                  </p>
                  <p className="text-xs mt-2" style={{ color: '#7CD4FD' }}>
                    +{stats?.items_added_this_week || 0} diese Woche
                  </p>
                </div>
                <BookOpen size={40} style={{ color: '#1EA0E6', opacity: 0.5 }} />
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Specs Card */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Fahrzeugdaten</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: '#E6EDF3' }}>
                    {stats?.total_vehicle_specs || 0}
                  </p>
                  <p className="text-xs mt-2" style={{ color: '#7CD4FD' }}>
                    +{stats?.specs_added_this_week || 0} diese Woche
                  </p>
                </div>
                <Database size={40} style={{ color: '#22D3EE', opacity: 0.5 }} />
              </div>
            </CardContent>
          </Card>

          {/* Completeness Card */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Vollständigkeit</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: '#E6EDF3' }}>
                    {stats?.complete_vehicle_specs || 0}/{stats?.total_vehicle_specs || 0}
                  </p>
                  <p className="text-xs mt-2" style={{ color: avgCompleteness >= 70 ? '#22C55E' : '#F59E0B' }}>
                    ⌀ {avgCompleteness.toFixed(0)}% vollständig
                  </p>
                </div>
                <CheckCircle size={40} style={{ color: '#22C55E', opacity: 0.5 }} />
              </div>
            </CardContent>
          </Card>

          {/* Success Rate Card */}
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>Success-Rate</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: '#E6EDF3' }}>
                    {successRate.toFixed(0)}%
                  </p>
                  <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                    aus {stats?.total_feedback || 0} Feedbacks
                  </p>
                </div>
                <TrendingUp size={40} style={{ color: successRate >= 80 ? '#22C55E' : '#F59E0B', opacity: 0.5 }} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feedback Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>Feedback-Übersicht</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ThumbsUp size={20} style={{ color: '#22C55E' }} />
                    <span style={{ color: '#94A3B8' }}>Positiv</span>
                  </div>
                  <span className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    {stats?.positive_feedback || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ThumbsDown size={20} style={{ color: '#EF4444' }} />
                    <span style={{ color: '#94A3B8' }}>Negativ</span>
                  </div>
                  <span className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    {stats?.negative_feedback || 0}
                  </span>
                </div>
                <div className="pt-4 border-t" style={{ borderColor: '#1F2937' }}>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${stats?.positive_feedback > 0 ? (stats.positive_feedback / stats.total_feedback * 100) : 0}%`,
                        backgroundColor: '#22C55E'
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Vehicles */}
          <Card className="lg:col-span-2" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>Top 10 Fahrzeuge (Diagnosen)</CardTitle>
            </CardHeader>
            <CardContent>
              {topVehicles.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topVehicles}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                    <XAxis dataKey="vehicle" stroke="#94A3B8" fontSize={12} angle={-45} textAnchor="end" height={80} />
                    <YAxis stroke="#94A3B8" />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}
                      labelStyle={{ color: '#E6EDF3' }}
                    />
                    <Bar dataKey="count" fill="#1EA0E6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ color: '#94A3B8' }}>Keine Daten verfügbar</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quality Trend */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Qualitäts-Entwicklung (30 Tage)</CardTitle>
          </CardHeader>
          <CardContent>
            {qualityTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={qualityTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="_id" stroke="#94A3B8" />
                  <YAxis stroke="#94A3B8" domain={[0, 1]} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}
                    labelStyle={{ color: '#E6EDF3' }}
                    formatter={(value) => value.toFixed(2)}
                  />
                  <Line type="monotone" dataKey="avg_quality" stroke="#1EA0E6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: '#94A3B8' }}>Keine Trend-Daten verfügbar</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
